/**
 * PuppetSelector Component (Task 7.2, 7.3)
 *
 * A dropdown selector for switching between puppets.
 * Uses Radix Select for accessible dropdown with keyboard navigation.
 *
 * Features:
 * - Displays active puppet name as trigger
 * - Lists all available puppets as options
 * - "Configure..." option navigates to configurator page
 * - Keyboard navigation (handled by Radix Select)
 */

import { useNavigate } from 'react-router-dom';
import { ChevronDown, Settings } from 'lucide-react';
import * as Select from '@radix-ui/react-select';
import { usePuppetStore } from '@/stores/puppetStore';

/** Special value for the Configure action */
const CONFIGURE_ACTION = '__configure__';

export function PuppetSelector() {
  const navigate = useNavigate();

  // Get puppet data and actions from store
  const puppets = usePuppetStore((state) => state.puppets);
  const activePuppetId = usePuppetStore((state) => state.activePuppetId);
  const setActivePuppetById = usePuppetStore(
    (state) => state.setActivePuppetById
  );

  // Find the active puppet
  const activePuppet = puppets.find((p) => p.id === activePuppetId);

  /**
   * Handle value change from Select
   * Either switches puppet or navigates to configurator page
   */
  const handleValueChange = (value: string) => {
    if (value === CONFIGURE_ACTION) {
      navigate('/configurator');
    } else {
      setActivePuppetById(value);
    }
  };

  // Don't render if no puppets exist
  if (puppets.length === 0) {
    return null;
  }

  return (
    <Select.Root
      value={activePuppetId || undefined}
      onValueChange={handleValueChange}
    >
      <Select.Trigger
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg
                   bg-dark-border hover:bg-dark-border/80
                   text-dark-text text-sm font-medium
                   transition-colors outline-none
                   focus:ring-2 focus:ring-body-head/50"
        aria-label="Select puppet"
      >
        <Select.Value placeholder="Select puppet">
          {activePuppet?.name || 'Select puppet'}
        </Select.Value>
        <Select.Icon>
          <ChevronDown
            className="h-4 w-4"
            style={{ color: 'var(--color-dark-text-muted)' }}
          />
        </Select.Icon>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          className="overflow-hidden rounded-lg border shadow-xl"
          style={{
            backgroundColor: 'var(--color-dark-panel)',
            borderColor: 'var(--color-dark-border)',
            zIndex: 'var(--z-index-modal)',
          }}
          position="popper"
          sideOffset={4}
        >
          <Select.Viewport className="p-1">
            {/* Puppet options */}
            {puppets.map((puppet) => (
              <Select.Item
                key={puppet.id}
                value={puppet.id}
                className="cursor-pointer rounded px-3 py-2 text-sm outline-none
                           transition-colors data-[highlighted]:bg-neutral-700
                           flex items-center justify-between"
                style={{ color: 'var(--color-dark-text)' }}
              >
                <Select.ItemText>{puppet.name}</Select.ItemText>
                {puppet.id === activePuppetId && (
                  <Select.ItemIndicator>
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: 'var(--color-body-head)' }}
                    />
                  </Select.ItemIndicator>
                )}
              </Select.Item>
            ))}

            {/* Separator */}
            <Select.Separator
              className="h-px my-1"
              style={{ backgroundColor: 'var(--color-dark-border)' }}
            />

            {/* Configure option */}
            <Select.Item
              value={CONFIGURE_ACTION}
              className="cursor-pointer rounded px-3 py-2 text-sm outline-none
                         transition-colors data-[highlighted]:bg-neutral-700
                         flex items-center gap-2"
              style={{ color: 'var(--color-dark-text-muted)' }}
            >
              <Settings className="h-4 w-4" />
              <Select.ItemText>Configure...</Select.ItemText>
            </Select.Item>
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
